import { Avatar, Icon } from "orcs-design-system";
import PropTypes from "prop-types";
import React from "react";
import styled, { css } from "styled-components";
import { themeGet } from "@styled-system/theme-get";

import { useWorkspaceFeatureFlags } from "src/contexts/global/WorkspaceContext";
import PersonPropType from "src/custom-prop-types/person";

const VACANT_SEAT_ICON = ["far", "id-badge"];

/**
 * Seat avatar language (MS-985 / option B):
 *   vacant — ghost face + amber dashed ring + id-badge glyph
 *   filled B1 (lists) — occupant photo, same dashed ring flush on the face
 *   filled B2 (profile header) — occupant photo inset inside the dashed ring
 * Amber + dashed matches vacant timeline spans (getVacantItemStyles).
 */
const dashedRing = css`
  box-sizing: border-box;
  border: 2px dashed ${themeGet("colors.warning")};
`;

const seatFace = css`
  ${dashedRing}
  background: transparent;
  background-image: none;
  color: ${themeGet("colors.warning")};

  svg {
    color: ${themeGet("colors.warning")};
  }
`;

// Regular Avatar DOM: wrapper > face div (Shape) > icon/initials.
// `$filled`: occupant exists but is hidden (org-chart people-off) — solid
// ring, same ghost face. Genuinely vacant stays dashed.
export const VacantSeatAvatar = styled(Avatar)`
  > div:first-child {
    ${seatFace}
    border: 2px ${({ $filled }) => ($filled ? "solid" : "dashed")}
      ${themeGet("colors.warning")};
  }
`;

// headerAvatar DOM: frame > inner (overflow hidden) > face (HeaderAvatarShape).
export const VacantSeatHeaderAvatar = styled(Avatar)`
  > div:first-child > div {
    ${seatFace}
  }
`;

// B1: ring sits on the photo (or initials Shape) — list / search / cards.
export const FilledSeatAvatar = styled(Avatar)`
  > img {
    ${dashedRing}
  }

  > div:first-child {
    ${dashedRing}
  }
`;

// B2: ring + inset gap on the header inner, photo shrinks to fill the rest.
export const FilledSeatHeaderAvatar = styled(Avatar)`
  > div:first-child {
    ${dashedRing}
    padding: 8px;
    background: ${themeGet("colors.surface")};
  }

  > div:first-child > img,
  > div:first-child > div {
    width: 100% !important;
    height: 100% !important;
    border-radius: 50%;
    box-sizing: border-box;
  }
`;

export const VacantSeatIcon = (props) => (
  <Icon icon={VACANT_SEAT_ICON} {...props} />
);

/**
 * Drop-in Avatar that renders the seat treatment when the given person is a
 * vacancy and position-backed vacancies are enabled; otherwise a plain
 * Avatar with the same props. For call sites that render people and seats
 * through one code path and have no feature-flag context of their own.
 */
export const SeatAwareAvatar = ({ person, ...avatarProps }) => {
  const { enablePositionBackedVacancies } = useWorkspaceFeatureFlags();

  if (enablePositionBackedVacancies && person?.isVacancy) {
    if (avatarProps.image) {
      return <FilledSeatAvatar {...avatarProps} />;
    }
    return (
      <VacantSeatAvatar
        {...avatarProps}
        image={undefined}
        initials={<VacantSeatIcon />}
      />
    );
  }

  return <Avatar {...avatarProps} />;
};

SeatAwareAvatar.propTypes = {
  person: PersonPropType,
};
